/**
 * Content Stage — Stage 3: Content quality validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.4
 *
 * Validates:
 * - Explanation quality (not generic, has didactic depth)
 * - Distractor explanations (present, not generic, reference misconceptions)
 * - Forbidden vocabulary per tier
 * - Question text length per tier
 * - Module-specific rules (from governance validator)
 * - Notation requirements (LaTeX, comma decimals, units)
 *
 * Split into focused sub-modules under contentValidators/:
 * - textQualityChecks.ts — forbidden vocab, jargon, sentence length, text length
 * - explanationChecks.ts — explanation quality, didactic depth, rule references
 * - distractorChecks.ts — distractor quality, D-QUAL heuristics
 * - notationChecks.ts — LaTeX, comma decimals, euro, superscript
 * - assessmentChecks.ts — CITO params, cognitive level, plus-level enforcement
 */

import type { ValidationIssue } from '../issueContract'
import type { Question } from '../../../content'
import { validateTextQuality } from './contentValidators/textQualityChecks'
import { validateExplanationQuality } from './contentValidators/explanationChecks'
import {
  validateDistractorQuality,
  checkDeadDistractorMetadata,
} from './contentValidators/distractorChecks'
import { validateSemanticDistractorCoherence } from './contentValidators/semanticDistractorChecks'
import { validateInteractivePayload } from './contentValidators/interactivePayloadChecks'
import { validateFillInBreadth } from './contentValidators/fillInBreadthChecks'
import { validateNotation } from './contentValidators/notationChecks'
import { validateAssessmentRequirements } from './contentValidators/assessmentChecks'
import { runSubValidator } from './runSubValidator'
import { STAGES } from '../stageDefinitions'

/**
 * Run content quality validation on a question.
 * Composes all content sub-validators into a single issue list.
 *
 * R7 (Sprint 5): each sub-validator is now crash-isolated so a single bad
 * seed cannot blind sibling checks within the same stage.
 */
export function runContentStage(question: Question): ValidationIssue[] {
  return [
    ...runSubValidator('validateTextQuality', 'content', STAGES.CONTENT, () =>
      validateTextQuality(question),
    ),
    ...runSubValidator(
      'validateExplanationQuality',
      'content',
      STAGES.CONTENT,
      () => validateExplanationQuality(question),
    ),
    ...runSubValidator(
      'validateDistractorQuality',
      'content',
      STAGES.CONTENT,
      () => validateDistractorQuality(question),
    ),
    ...runSubValidator(
      'checkDeadDistractorMetadata',
      'content',
      STAGES.CONTENT,
      () => checkDeadDistractorMetadata(question),
    ),
    ...runSubValidator(
      'validateSemanticDistractorCoherence',
      'content',
      STAGES.CONTENT,
      () => validateSemanticDistractorCoherence(question),
    ),
    ...runSubValidator(
      'validateInteractivePayload',
      'content',
      STAGES.CONTENT,
      () => validateInteractivePayload(question),
    ),
    ...runSubValidator('validateFillInBreadth', 'content', STAGES.CONTENT, () =>
      validateFillInBreadth(question),
    ),
    ...runSubValidator('validateNotation', 'content', STAGES.CONTENT, () =>
      validateNotation(question),
    ),
    ...runSubValidator(
      'validateAssessmentRequirements',
      'content',
      STAGES.CONTENT,
      () => validateAssessmentRequirements(question),
    ),
  ]
}
